/**
 * A bounded, self-scrubbing record of what happened before a failure (IR-1).
 *
 * Nothing here is sent anywhere. It is an in-memory ring buffer that a
 * diagnostics bundle reads when a user files a report; if no report is ever
 * filed, nothing leaves the machine and the buffer dies with the tab.
 *
 * ## Three properties, each load-bearing
 *
 * 1. **Bounded twice.** By entry count AND by bytes. A count alone is not a
 *    bound: one pathological stack can be larger than the other 299 entries
 *    combined, and the entries it evicts are the ones explaining how the
 *    failure was reached.
 * 2. **Scrubbed on the way IN, not on the way out.** `scrubGeneric` runs at
 *    capture, before any project context exists, because breadcrumbs are
 *    recorded long before `clientInfo` is loaded. The bundle scrubs AGAIN at
 *    assembly with project context. Neither pass is sufficient alone: the
 *    first cannot know the client's phone number, and the second is too late
 *    for a buffer that already holds a credential.
 * 3. **Taps only, never call sites.** Every source is an existing eventBus
 *    event or a global handler. No module is edited to "add a breadcrumb", so
 *    there is no instrumentation to drift, and removing this file removes the
 *    feature completely.
 */

import { eventBus } from './events.js';
import { scrubGeneric } from '../shared/pii-scrub.js';
import { normalizeErrorPayload } from '../shared/error-payload.js';

/** Entry cap. Roughly a few minutes of active editing. */
export const MAX_BREADCRUMBS = 300;
/** Byte ceiling for the whole buffer, measured on the serialized entries. */
export const MAX_BREADCRUMB_BYTES = 256 * 1024;
/** Per-entry cap, so one entry can never dominate the ceiling. */
export const MAX_ENTRY_BYTES = 8 * 1024;

const buffer = [];
let bufferBytes = 0;
let installed = false;
let teardowns = [];

/** Serialized size of one entry, the same measure the ceiling is kept against. */
function sizeOf(entry) {
  try {
    return JSON.stringify(entry).length;
  } catch {
    return MAX_ENTRY_BYTES;
  }
}

function scrubDeep(value, depth = 0) {
  if (depth > 4) return '[depth]';
  if (typeof value === 'string') return scrubGeneric(value);
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => scrubDeep(v, depth + 1));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = scrubDeep(v, depth + 1);
    return out;
  }
  return value;
}

/**
 * Record one breadcrumb. Never throws: a diagnostics buffer that can break the
 * app it observes is worse than no buffer.
 */
export function recordBreadcrumb(kind, data = {}, { now = Date.now() } = {}) {
  try {
    const entry = { kind: String(kind || 'unknown'), at: now, ...scrubDeep(data) };

    // A repeat of the immediately-preceding identical breadcrumb becomes a
    // count rather than a new entry. A tight loop of the same failure would
    // otherwise flush the whole buffer with one message.
    const prev = buffer[buffer.length - 1];
    if (prev && prev.kind === entry.kind && sameShape(prev, entry)) {
      prev.count = (prev.count || 1) + 1;
      prev.lastAt = now;
      return prev;
    }

    let size = sizeOf(entry);
    if (size > MAX_ENTRY_BYTES) {
      // Truncate rather than drop — and SAY SO. A silently clipped entry reads
      // as complete, which is worse than an absent one.
      if (typeof entry.stack === 'string') entry.stack = entry.stack.slice(0, 1200);
      if (typeof entry.message === 'string') entry.message = entry.message.slice(0, 240);
      entry.truncated = true;
      size = Math.min(sizeOf(entry), MAX_ENTRY_BYTES);
    }

    buffer.push(entry);
    bufferBytes += size;
    evict();
    return entry;
  } catch {
    return null;
  }
}

/** Same entry modulo timing/counting fields. */
function sameShape(a, b) {
  const skip = new Set(['at', 'count', 'lastAt']);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)].filter((k) => !skip.has(k)));
  for (const k of keys) {
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) return false;
  }
  return true;
}

function evict() {
  while (buffer.length > MAX_BREADCRUMBS || bufferBytes > MAX_BREADCRUMB_BYTES) {
    const dropped = buffer.shift();
    if (!dropped) break;
    bufferBytes -= sizeOf(dropped);
    if (bufferBytes < 0) bufferBytes = 0;
  }
}

/** A copy. Callers must not be able to mutate the buffer through the snapshot. */
export function breadcrumbSnapshot() {
  return buffer.map((entry) => {
    if (typeof structuredClone === 'function') return structuredClone(entry);
    // Entries are already JSON-bounded/sanitized on capture; this fallback is
    // for older WebViews without structuredClone, not for arbitrary values.
    return JSON.parse(JSON.stringify(entry));
  });
}

export function breadcrumbStats() {
  return { count: buffer.length, bytes: bufferBytes };
}

export function _resetBreadcrumbsForTests() {
  buffer.length = 0;
  bufferBytes = 0;
  for (const fn of teardowns) {
    try {
      fn();
    } catch {
      /* teardown is best-effort */
    }
  }
  teardowns = [];
  installed = false;
}

/**
 * Subscribe to everything already being emitted. Idempotent.
 *
 * `win` and `bus` are injected so the taps can be driven in a test without a
 * real browser, and so installing twice cannot double-subscribe.
 */
export function installBreadcrumbTaps({ win = globalThis, bus = eventBus } = {}) {
  if (installed) return () => {};
  installed = true;

  const onBus = (event, fn) => {
    bus.on(event, fn);
    teardowns.push(() => bus.off(event, fn));
  };

  // ── the central error funnel ──
  onBus('report-error', (p) =>
    recordBreadcrumb('error', {
      scope: p?.scope,
      message: p?.message,
      detail: p?.detail,
      level: p?.level,
      user: p?.user
    })
  );
  // reportError DEDUPES before it emits, so a failure repeating inside its
  // 8s window emits nothing at all — and a tight repeating error is exactly
  // the kind worth reporting. The suppressed branch emits its own event so the
  // repeat becomes a COUNT instead of silence.
  onBus('report-error:suppressed', (p) =>
    recordBreadcrumb('error-repeat', { scope: p?.scope, message: p?.message })
  );

  // ── state and lifecycle ──
  onBus('store:write-rejected', (p) =>
    recordBreadcrumb('write-rejected', { operation: p?.operation })
  );
  onBus('writability-changed', (p) =>
    recordBreadcrumb('writability', { writable: p?.writable, blocker: p?.blocker })
  );
  onBus('history-changed', (p) =>
    recordBreadcrumb('history', { source: p?.source, label: p?.label })
  );
  onBus('autosave:dirty', () => recordBreadcrumb('autosave-dirty'));
  onBus('project:restored', () => recordBreadcrumb('project-restored'));

  // ── the preview iframe's own errors, already sanitized on that side ──
  onBus('iframe:iframe-error', (p) =>
    recordBreadcrumb('iframe-error', {
      name: p?.name,
      message: p?.message,
      source: p?.source,
      line: p?.line
    })
  );

  // ── the parent window's own errors: genuinely new in IR-1 ──
  const ctx = () => ({
    origin: win?.location?.origin || '',
    href: win?.location?.href || ''
  });
  if (typeof win?.addEventListener === 'function') {
    const onError = (event) =>
      recordBreadcrumb('window-error', normalizeErrorPayload('error', event, ctx()));
    const onRejection = (event) =>
      recordBreadcrumb('window-error', normalizeErrorPayload('unhandledrejection', event, ctx()));
    win.addEventListener('error', onError);
    win.addEventListener('unhandledrejection', onRejection);
    teardowns.push(() => win.removeEventListener('error', onError));
    teardowns.push(() => win.removeEventListener('unhandledrejection', onRejection));
  }

  return () => _resetBreadcrumbsForTests();
}

/**
 * Wrap `fetch` to record request outcomes.
 *
 * PATH ONLY — the query and hash are dropped, because they carry tokens and
 * addresses and answer no diagnostic question that the path does not. Never
 * bodies, never headers. Separate from `installBreadcrumbTaps` because
 * monkeypatching a global is a bigger commitment than subscribing to an event,
 * and a caller should have to ask for it explicitly.
 */
export function installFetchTap({ win = globalThis, now = () => Date.now() } = {}) {
  const original = win?.fetch;
  if (typeof original !== 'function') return () => {};

  win.fetch = async function tappedFetch(input, init) {
    const started = now();
    let path;
    try {
      const raw = typeof input === 'string' ? input : input?.url || '';
      path = new URL(raw, win.location?.href || 'https://local/').pathname;
    } catch {
      // An unparseable target still gets a breadcrumb — the status and timing
      // are the diagnostic part — it just has no path.
      path = '';
    }
    try {
      const response = await original.call(this, input, init);
      recordBreadcrumb('fetch', {
        path,
        method: init?.method || 'GET',
        status: response?.status ?? 0,
        ms: now() - started
      });
      return response;
    } catch (err) {
      recordBreadcrumb('fetch', {
        path,
        method: init?.method || 'GET',
        status: 0,
        ms: now() - started,
        failed: String(err?.message || err).slice(0, 240)
      });
      throw err;
    }
  };
  return () => {
    win.fetch = original;
  };
}
